import React, { useEffect, useRef, useState } from "react";
import { Camera, Scan } from "lucide-react";

export default function VIPScanner() {
    const inputRef = useRef(null);
    const [scanning, setScanning] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [messageType, setMessageType] = useState("error");
    const [successMessage, setSuccessMessage] = useState(null);
    const [scannedUserName, setScannedUserName] = useState("");
    const [showWelcome, setShowWelcome] = useState(false);
    const [scanCount, setScanCount] = useState(0);
        const [currentTime, setCurrentTime] = useState(new Date());

    // Update clock every second
    useEffect(() => {
        const timer = setInterval(() => setCurrentTime(new Date()), 1000);
        return () => clearInterval(timer);
    }, [])

    const QR_CODE_REGEX = /\/registration\/2025\/([A-Za-z0-9]+)(?:[/?#]|$)/;

    const ERROR_MESSAGES = {
        INVALID_QR: "Invalid QR code format.",
        NOT_REGISTERED: "This link is not valid.",
        NOT_ACTIVATED: "Your Smart Card needs to be activated.",
        ALREADY_MARKED: "Attendance already marked for today.",
        NOT_REGISTERED_FOR_EVENT: "This Smart Card is not registered for today's event.",
        SAVE_ERROR: "An error occurred while saving the scan data.",
        NETWORK_ERROR: "Network error. Please check your connection.",
    };

    const MESSAGE_TYPES = {
        INVALID_QR: "error",
        NOT_REGISTERED: "warning",
        NOT_ACTIVATED: "info",
        ALREADY_MARKED: "warning",
        NOT_REGISTERED_FOR_EVENT: "warning",
        SAVE_ERROR: "error",
        NETWORK_ERROR: "error",
    };

    useEffect(() => {
        const focus = () => inputRef.current?.focus();
        focus();
        window.addEventListener("click", focus);
        window.addEventListener("focus", focus);
        return () => {
            window.removeEventListener("click", focus);
            window.removeEventListener("focus", focus);
        };
    }, []);

    const handleKeyDown = (e) => {
        if (e.key === "Enter") {
            const code = e.target.value.trim();
            if (code) {
                submitCode(code);
                e.target.value = "";
            }
            e.preventDefault();
        }
    };

    const handlePaste = (e) => {
        const pasted = (e.clipboardData || window.clipboardData).getData("text");
        if (pasted) {
            e.preventDefault();
            submitCode(pasted.trim());
            e.target.value = "";
        }
    };

    const submitCode = async (decodedText) => {
        setScanning(true);
        setErrorMessage(null);
        setSuccessMessage(null);
        setShowWelcome(false);

        const match = decodedText.match(QR_CODE_REGEX);
        if (!match) {
            showError(ERROR_MESSAGES.INVALID_QR, MESSAGE_TYPES.INVALID_QR);
            setScanning(false);
            inputRef.current?.focus();
            return;
        }

        const hexaCode = match[1];

        if (!hexaCode || hexaCode.length === 0) {
            showError(ERROR_MESSAGES.INVALID_QR, MESSAGE_TYPES.INVALID_QR);
            setScanning(false);
            inputRef.current?.focus();
            return;
        }

        try {
            const response = await fetch("/api/vip/scan/data", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    hexaCode,
                    scannedBy: "Manual Scanner",
                    deviceInfo: navigator.userAgent
                }),
            });

            const data = await response.json();

            if (response.ok && data.status === "success") {
                const userName = data?.data?.firstName || "User";
                setScannedUserName(userName);
                setShowWelcome(true);
                setScanCount(prev => prev + 1);
                showSuccess(userName);
            } else {
                let errorMsg = ERROR_MESSAGES.SAVE_ERROR;
                let msgType = MESSAGE_TYPES.SAVE_ERROR;

                switch (data.status) {
                    case "not_registered":
                        errorMsg = ERROR_MESSAGES.NOT_REGISTERED;
                        msgType = MESSAGE_TYPES.NOT_REGISTERED;
                        break;
                    case "registration_incomplete":
                        errorMsg = ERROR_MESSAGES.NOT_ACTIVATED;
                        msgType = MESSAGE_TYPES.NOT_ACTIVATED;
                        break;
                    case "already_marked":
                        errorMsg = ERROR_MESSAGES.ALREADY_MARKED;
                        msgType = MESSAGE_TYPES.ALREADY_MARKED;
                        break;
                    case "not_registered_for_event":
                        errorMsg = ERROR_MESSAGES.NOT_REGISTERED_FOR_EVENT;
                        msgType = MESSAGE_TYPES.NOT_REGISTERED_FOR_EVENT;
                        break;
                    default:
                        errorMsg = data.message || ERROR_MESSAGES.SAVE_ERROR;
                        msgType = "error";
                }

                showError(errorMsg, msgType);
            }
        } catch (err) {
            console.error("Error saving scan data:", err);
            showError(ERROR_MESSAGES.NETWORK_ERROR, MESSAGE_TYPES.NETWORK_ERROR);
        } finally {
            setScanning(false);
            inputRef.current?.focus();
        }
    };

    const showSuccess = (userName) => {
        setSuccessMessage(userName);
        setErrorMessage(null);

        setTimeout(() => {
            setShowWelcome(false);
        }, 4000);
    };

    const showError = (message, type = "error") => {
        setErrorMessage(message);
        setMessageType(type);
        setSuccessMessage(null);

        setTimeout(() => {
            setErrorMessage(null);
        }, 3000);
    };

    const getMessageConfig = (type) => {
        switch(type) {
            case "error":
                return {
                    icon: "✗",
                    title: "प्रवेश नाही",
                    borderColor: "#ff4444",
                    iconColor: "#ff4444",
                    titleColor: "#8B0000",
                    textColor: "#ff4444"
                };
            case "warning":
                return {
                    icon: "⚠",
                    title: "सूचना",
                    borderColor: "#ff9800",
                    iconColor: "#ff9800",
                    titleColor: "#e65100",
                    textColor: "#ff9800"
                };
           case "info":
                return {
                    icon: "ℹ",
                    title: "माहिती",
                    borderColor: "#9C27B0",
                    iconColor: "#9C27B0",
                    titleColor: "#6A1B9A",
                    textColor: "#9C27B0"
                };
            default:
                return {
                    icon: "✗",
                    title: "प्रवेश नाही",
                    borderColor: "#ff4444",
                    iconColor: "#ff4444",
                    titleColor: "#8B0000",
                    textColor: "#ff4444"
                };
        }
    };

    const messageConfig = getMessageConfig(messageType);

    return (
        <div style={styles.container}>
            <input
                ref={inputRef}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                style={styles.hiddenInput}
                autoComplete="off"
                autoFocus
                aria-hidden="true"
            />

            {/* <div style={styles.header}>
                <img src="/khasdar.jpg" alt="Logo" style={styles.logo} />
                <div style={styles.eventInfo}>
                    <h1 style={styles.eventTitle}>खासदार सांस्कृतिक महोत्सव 2025</h1>
                    <p style={styles.eventDate}>७ ते १८ नोव्हेंबर २०२५</p>
                </div>
            </div> */}

             <div style={styles.header}>
                <img src="/khasdar.jpg" alt="Logo" style={styles.logo} />
                  {/* <div className="text-right">
                    <div className="text-3xl font-bold text-white">
                        {currentTime.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                    </div>
                    <div className="text-slate-400 text-lg">
                        {currentTime.toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                    </div>
                </div> */}
                <div style={styles.eventInfo}>
                    <h1 style={styles.eventTitle}>खासदार सांस्कृतिक महोत्सव 2025</h1>
                    <p style={styles.eventDate}>७ ते १८ नोव्हेंबर २०२५</p>
                </div>
              
            </div>

            {!showWelcome && !errorMessage && (
                <div style={styles.scannerBox}>
                    <div style={styles.scanAnimationContainer}>
                        <div style={styles.cardIconContainer}>
                            <Camera size={110} color="#8B0000" strokeWidth={2} />
                        </div>
                        <div style={styles.scanLineContainer}>
                            <div style={styles.scanLine}></div>
                        </div>
                        <div style={styles.scanIconContainer}>
                            <Scan size={45} color="#FFD700" strokeWidth={2.5} />
                        </div>
                    </div>
                    <p style={styles.scannerText}>
                        {scanning ? "प्रक्रिया सुरू आहे..." : "कृपया कार्ड स्कॅन करा"}
                    </p>
                    <div style={styles.scannerIndicator}>
                        {scanning ? (
                            <div style={styles.pulse}></div>
                        ) : (
                            <div style={styles.readyDot}></div>
                        )}
                    </div>
                    {scanCount > 0 && (
                        <div style={styles.scanCounter}>
                            आजचे प्रवेश: {scanCount}
                        </div>
                    )}
                </div>
            )}

            {showWelcome && successMessage && (
                <div style={styles.welcomeContainer}>
                    <div style={styles.welcomeCard}>
                        <div style={styles.tickLarge}>✓</div>
                        <h2 style={styles.welcomeText}>स्वागत आहे!</h2>
                        <h1 style={styles.userName}>{successMessage}</h1>
                        <div style={styles.successBadge}>
                            <span style={styles.badgeIcon}>✓</span>
                            Entry Confirmed
                        </div>
                        <p style={styles.welcomeSubtext}>आपले स्वागत आहे खासदार सांस्कृतिक महोत्सवात</p>
                    </div>
                </div>
            )}

            {errorMessage && (
                <div style={styles.errorContainer}>
                    <div style={{
                        ...styles.errorCard,
                        border: `4px solid ${messageConfig.borderColor}`
                    }}>
                        <div style={{
                            ...styles.crossLarge,
                            color: messageConfig.iconColor,
                            textShadow: `0 4px 10px ${messageConfig.iconColor}66`
                        }}>
                            {messageConfig.icon}
                        </div>
                        <h2 style={{
                            ...styles.errorTitle,
                            color: messageConfig.titleColor
                        }}>
                            {messageConfig.title}
                        </h2>
                        <p style={{
                            ...styles.errorText,
                            color: messageConfig.textColor
                        }}>
                            {errorMessage}
                        </p>
                    </div>
                </div>
            )}

            <div style={styles.footerContainer}>
                <div style={styles.poweredByBox}>
                    <span style={styles.poweredByText}>Powered By</span>
                    <img
                        src="/bharatOnline.png"
                        alt="Bharat Online"
                        crossOrigin="anonymous"
                        style={styles.footerLogo}
                    />
                    <span style={styles.companyName}>Bharat Online</span>
                </div>
            </div>
        </div>
    );
}

const styles = {
    container: {
        textAlign: "center",
        fontFamily: "'Segoe UI', 'Noto Sans Devanagari', Arial, sans-serif",
        padding: "20px",
        paddingBottom: "100px",
        background: "radial-gradient(ellipse at top, #8B0000, #5C0000, #2D0000)",
        color: "#fff",
        minHeight: "100vh",
        position: "relative",
        overflow: "hidden",
    },
    hiddenInput: {
        opacity: 0,
        position: "absolute",
        left: "-9999px",
        width: "1px",
        height: "1px",
    },
    header: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        marginBottom: "30px",
        position: "relative",
    },
    logo: {
        width: "120px",
        height: "120px",
        borderRadius: "50%",
        border: "4px solid #FFD700",
        boxShadow: "0px 4px 20px rgba(255, 215, 0, 0.5)",
        marginBottom: "15px",
    },
    eventInfo: {
        background: "linear-gradient(135deg, rgba(139, 0, 0, 0.9), rgba(92, 0, 0, 0.9))",
        padding: "25px 50px",
        borderRadius: "15px",
        border: "2px solid #FFD700",
        boxShadow: "0 8px 32px rgba(0, 0, 0, 0.4)",
    },
    eventTitle: {
        fontSize: "32px",
        fontWeight: "bold",
        margin: "0 0 10px 0",
        color: "#FFD700",
        textShadow: "2px 2px 4px rgba(0, 0, 0, 0.5)",
    },
    eventDate: {
        fontSize: "20px",
        margin: 0,
        color: "#FFF",
        opacity: 0.95,
    },
    scannerBox: {
        width: "100%",
        maxWidth: "500px",
        margin: "40px auto",
        padding: "60px 40px",
        background: "linear-gradient(145deg, rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0.9))",
        borderRadius: "25px",
        boxShadow: "0px 10px 40px rgba(0, 0, 0, 0.5)",
        border: "3px solid #FFD700",
    },
    scannerIcon: {
        fontSize: "120px",
        marginBottom: "20px",
    },
    scanAnimationContainer: {
        position: "relative",
        marginBottom: "25px",
        height: "140px",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
    },
    cardIconContainer: {
        filter: "drop-shadow(0 6px 12px rgba(139, 0, 0, 0.2))",
        animation: "float 3s ease-in-out infinite",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1,
    },
    scanLineContainer: {
        position: "absolute",
        width: "100%",
        height: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 2,
        pointerEvents: "none",
    },
    scanLine: {
        width: "120px",
        height: "3px",
        background: "linear-gradient(90deg, transparent, #FFD700, transparent)",
        boxShadow: "0 0 10px #FFD700, 0 0 20px #FFD700",
        animation: "scan 2.5s ease-in-out infinite",
    },
    scanIconContainer: {
        position: "absolute",
        bottom: "10px",
        right: "calc(50% - 60px)",
        animation: "pulse 2s ease-in-out infinite",
        filter: "drop-shadow(0 2px 8px rgba(255, 215, 0, 0.5))",
    },
    scannerText: {
        fontSize: "40px",
        fontWeight: "700",
        color: "#8B0000",
        margin: "15px 0",
    },
    scannerIndicator: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        marginTop: "20px",
    },
    pulse: {
        width: "30px",
        height: "30px",
        borderRadius: "50%",
        background: "#FF6B00",
        animation: "pulse 1.5s ease-in-out infinite",
        boxShadow: "0 0 20px rgba(255, 107, 0, 0.6)",
    },
    readyDot: {
        width: "30px",
        height: "30px",
        borderRadius: "50%",
        background: "#28a745",
        boxShadow: "0 0 20px rgba(40, 167, 69, 0.6)",
    },
    scanCounter: {
        marginTop: "25px",
        fontSize: "20px",
        color: "#8B0000",
        fontWeight: "600",
        padding: "12px 24px",
        background: "rgba(255, 215, 0, 0.2)",
        borderRadius: "25px",
        display: "inline-block",
    },
    welcomeContainer: {
        width: "100%",
        maxWidth: "550px",
        margin: "40px auto",
        animation: "scaleIn 0.5s ease-out",
    },
    welcomeCard: {
        background: "linear-gradient(145deg, rgba(255, 255, 255, 0.98), rgba(255, 255, 255, 0.95))",
        padding: "60px 40px",
        borderRadius: "30px",
        boxShadow: "0px 15px 50px rgba(0, 0, 0, 0.6)",
        border: "4px solid #FFD700",
        position: "relative",
        overflow: "hidden",
    },
    tickLarge: {
        fontSize: "120px",
        color: "#28a745",
        fontWeight: "bold",
        marginBottom: "20px",
        textShadow: "0 4px 10px rgba(40, 167, 69, 0.4)",
        animation: "scaleIn 0.4s ease-out",
    },
    welcomeText: {
        fontSize: "36px",
        color: "#8B0000",
        margin: "10px 0",
        fontWeight: "bold",
    },
    userName: {
        fontSize: "48px",
        color: "#004526",
        margin: "20px 0",
        fontWeight: "bold",
        textShadow: "2px 2px 4px rgba(0, 0, 0, 0.1)",
        wordBreak: "break-word",
    },
    successBadge: {
        display: "inline-block",
        background: "#28a745",
        color: "#fff",
        padding: "18px 45px",
        borderRadius: "50px",
        fontSize: "22px",
        fontWeight: "bold",
        margin: "20px 0",
        boxShadow: "0 4px 15px rgba(40, 167, 69, 0.4)",
    },
    badgeIcon: {
        marginRight: "10px",
        fontSize: "26px",
    },
    welcomeSubtext: {
        fontSize: "20px",
        color: "#555",
        marginTop: "15px",
        fontStyle: "italic",
    },
    errorContainer: {
        width: "100%",
        maxWidth: "550px",
        margin: "40px auto",
        animation: "slideIn 0.4s ease-out",
    },
    errorCard: {
        background: "linear-gradient(145deg, rgba(255, 255, 255, 0.98), rgba(255, 255, 255, 0.95))",
        padding: "60px 40px",
        borderRadius: "30px",
        boxShadow: "0px 15px 50px rgba(0, 0, 0, 0.6)",
    },
    crossLarge: {
        fontSize: "120px",
        fontWeight: "bold",
        marginBottom: "20px",
    },
    errorTitle: {
        fontSize: "36px",
        margin: "10px 0 20px 0",
        fontWeight: "bold",
    },
    errorText: {
        fontSize: "22px",
        fontWeight: "600",
        margin: "10px 0",
    },
    footerContainer: {
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        display: "flex",
        justifyContent: "center",
        width: "100%",
        padding: "0",
    },
    poweredByBox: {
        background: "transparent",
        width: "100%",
        padding: "20px",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        gap: "12px",
    },
    poweredByText: {
        color: "#fff",
        fontSize: "18px",
        fontWeight: "500",
    },
    footerLogo: {
        height: "35px",
        filter: "brightness(1.1)",
    },
    companyName: {
        color: "#fff",
        fontSize: "18px",
        fontWeight: "600",
    },
};

const styleSheet = document.createElement("style");
styleSheet.textContent = `
    @keyframes pulse {
        0%, 100% {
            transform: scale(1);
            opacity: 1;
        }
        50% {
            transform: scale(1.4);
            opacity: 0.7;
        }
    }
    @keyframes scaleIn {
        from {
            transform: scale(0);
            opacity: 0;
        }
        to {
            transform: scale(1);
            opacity: 1;
        }
    }
    @keyframes slideIn {
        from {
            transform: translateY(-30px);
            opacity: 0;
        }
        to {
            transform: translateY(0);
            opacity: 1;
        }
    }
    @keyframes float {
        0%, 100% {
            transform: translateY(0px);
        }
        50% {
            transform: translateY(-10px);
        }
    }
    @keyframes scan {
        0%, 100% {
            transform: translateY(-60px);
            opacity: 0;
        }
        10% {
            opacity: 1;
        }
        90% {
            opacity: 1;
        }
        100% {
            transform: translateY(60px);
            opacity: 0;
        }
    }
`;
document.head.appendChild(styleSheet);




// import React, { useEffect, useRef, useState } from "react";
// import { Camera, Scan } from "lucide-react";

// export default function VIPScanner() {
//     const inputRef = useRef(null);
//     const [scanning, setScanning] = useState(false);
//     const [errorMessage, setErrorMessage] = useState(null);
//     const [successMessage, setSuccessMessage] = useState(null);
//     const [scannedUserName, setScannedUserName] = useState("");
//     const [showWelcome, setShowWelcome] = useState(false);
//     const [scanCount, setScanCount] = useState(0);
    // const [currentTime, setCurrentTime] = useState(new Date());

    // // Update clock every second
    // useEffect(() => {
    //     const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    //     return () => clearInterval(timer);
    // }, [])
//     const QR_CODE_REGEX = /\/registration\/2025\/([A-Za-z0-9]+)(?:[/?#]|$)/;

//     const ERROR_MESSAGES = {
//         INVALID_QR: "Invalid QR code format.",
//         NOT_REGISTERED: "This link is not valid.",
//         NOT_ACTIVATED: "The Smart Card is not activated yet.",
//         ALREADY_MARKED: "Attendance already marked for today.",
//         SAVE_ERROR: "An error occurred while saving the scan data.",
//         NETWORK_ERROR: "Network error. Please check your connection.",
//     };

//     useEffect(() => {
//         const focus = () => inputRef.current?.focus();
//         focus();
//         window.addEventListener("click", focus);
//         window.addEventListener("focus", focus);
//         return () => {
//             window.removeEventListener("click", focus);
//             window.removeEventListener("focus", focus);
//         };
//     }, []);

//     const handleKeyDown = (e) => {
//         if (e.key === "Enter") {
//             const code = e.target.value.trim();
//             if (code) {
//                 submitCode(code);
//                 e.target.value = "";
//             }
//             e.preventDefault();
//         }
//     };

//     const handlePaste = (e) => {
//         const pasted = (e.clipboardData || window.clipboardData).getData("text");
//         if (pasted) {
//             e.preventDefault();
//             submitCode(pasted.trim());
//             e.target.value = "";
//         }
//     };

//     const submitCode = async (decodedText) => {
//         setScanning(true);
//         setErrorMessage(null);
//         setSuccessMessage(null);
//         setShowWelcome(false);

//         const match = decodedText.match(QR_CODE_REGEX);
//         if (!match) {
//             showError(ERROR_MESSAGES.INVALID_QR);
//             setScanning(false);
//             inputRef.current?.focus();
//             return;
//         }

//         const hexaCode = match[1];

//         if (!hexaCode || hexaCode.length === 0) {
//             showError(ERROR_MESSAGES.INVALID_QR);
//             setScanning(false);
//             inputRef.current?.focus();
//             return;
//         }

//         try {
//             const response = await fetch("/api/vip/scan/data", {
//                 method: "POST",
//                 headers: { "Content-Type": "application/json" },
//                 body: JSON.stringify({
//                     hexaCode,
//                     scannedBy: "Manual Scanner",
//                     deviceInfo: navigator.userAgent
//                 }),
//             });

//             const data = await response.json();

//             if (response.ok && data.status === "success") {
//                 const userName = data?.data?.firstName || "User";
//                 setScannedUserName(userName);
//                 setShowWelcome(true);
//                 setScanCount(prev => prev + 1);
//                 showSuccess(userName);
//             } else {
//                 let errorMsg = ERROR_MESSAGES.SAVE_ERROR;

//                 switch (data.status) {
//                     case "not_registered":
//                         errorMsg = ERROR_MESSAGES.NOT_REGISTERED;
//                         break;
//                     case "registration_incomplete":
//                         errorMsg = ERROR_MESSAGES.NOT_ACTIVATED;
//                         break;
//                     case "already_marked":
//                         errorMsg = ERROR_MESSAGES.ALREADY_MARKED;
//                         break;
//                     default:
//                         errorMsg = data.message || ERROR_MESSAGES.SAVE_ERROR;
//                 }

//                 showError(errorMsg);
//             }
//         } catch (err) {
//             console.error("Error saving scan data:", err);
//             showError(ERROR_MESSAGES.NETWORK_ERROR);
//         } finally {
//             setScanning(false);
//             inputRef.current?.focus();
//         }
//     };

//     const showSuccess = (userName) => {
//         setSuccessMessage(userName);
//         setErrorMessage(null);

//         setTimeout(() => {
//             setShowWelcome(false);
//         }, 4000);
//     };

//     const showError = (message) => {
//         setErrorMessage(message);
//         setSuccessMessage(null);

//         setTimeout(() => {
//             setErrorMessage(null);
//         }, 3000);
//     };

//     return (
//         <div style={styles.container}>
//             <input
//                 ref={inputRef}
//                 onKeyDown={handleKeyDown}
//                 onPaste={handlePaste}
//                 style={styles.hiddenInput}
//                 autoComplete="off"
//                 autoFocus
//                 aria-hidden="true"
//             />

            // <div style={styles.header}>
            //     <img src="/khasdar.jpg" alt="Logo" style={styles.logo} />
            //       <div className="text-right">
            //         <div className="text-3xl font-bold text-white">
            //             {currentTime.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            //         </div>
            //         <div className="text-slate-400 text-lg">
            //             {currentTime.toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            //         </div>
            //     </div>
            //     <div style={styles.eventInfo}>
            //         <h1 style={styles.eventTitle}>खासदार सांस्कृतिक महोत्सव 2025</h1>
            //         <p style={styles.eventDate}>७ ते १८ नोव्हेंबर २०२५</p>
            //     </div>
              
            // </div>

//             {!showWelcome && !errorMessage && (
//                 <div style={styles.scannerBox}>
//                     <div style={styles.scanAnimationContainer}>
//                         <div style={styles.cardIconContainer}>
//                             <Camera size={110} color="#8B0000" strokeWidth={2} />
//                         </div>
//                         <div style={styles.scanLineContainer}>
//                             <div style={styles.scanLine}></div>
//                         </div>
//                         <div style={styles.scanIconContainer}>
//                             <Scan size={45} color="#FFD700" strokeWidth={2.5} />
//                         </div>
//                     </div>
//                     <p style={styles.scannerText}>
//                         {scanning ? "प्रक्रिया सुरू आहे..." : "कृपया कार्ड स्कॅन करा"}
//                     </p>
//                     <div style={styles.scannerIndicator}>
//                         {scanning ? (
//                             <div style={styles.pulse}></div>
//                         ) : (
//                             <div style={styles.readyDot}></div>
//                         )}
//                     </div>
//                     {scanCount > 0 && (
//                         <div style={styles.scanCounter}>
//                             आजचे प्रवेश: {scanCount}
//                         </div>
//                     )}
//                 </div>
//             )}

//             {showWelcome && successMessage && (
//                 <div style={styles.welcomeContainer}>
//                     <div style={styles.welcomeCard}>
//                         <div style={styles.tickLarge}>✓</div>
//                         <h2 style={styles.welcomeText}>स्वागत आहे!</h2>
//                         <h1 style={styles.userName}>{successMessage}</h1>
//                         <div style={styles.successBadge}>
//                             <span style={styles.badgeIcon}>✓</span>
//                             Entry Confirmed
//                         </div>
//                         <p style={styles.welcomeSubtext}>आपले स्वागत आहे खासदार सांस्कृतिक महोत्सवात</p>
//                     </div>
//                 </div>
//             )}

//             {errorMessage && (
//                 <div style={styles.errorContainer}>
//                     <div style={styles.errorCard}>
//                         <div style={styles.crossLarge}>✗</div>
//                         <h2 style={styles.errorTitle}>प्रवेश नाही</h2>
//                         <p style={styles.errorText}>{errorMessage}</p>
//                     </div>
//                 </div>
//             )}

//             <div style={styles.footerContainer}>
//                 <div style={styles.poweredByBox}>
//                     <span style={styles.poweredByText}>Powered By</span>
//                     <img
//                         src="/bharatOnline.png"
//                         alt="Bharat Online"
//                         crossOrigin="anonymous"
//                         style={styles.footerLogo}
//                     />
//                     <span style={styles.companyName}>Bharat Online</span>
//                 </div>
//             </div>
//         </div>
//     );
// }

// const styles = {
//     container: {
//         textAlign: "center",
//         fontFamily: "'Segoe UI', 'Noto Sans Devanagari', Arial, sans-serif",
//         padding: "20px",
//         paddingBottom: "100px",
//         background: "radial-gradient(ellipse at top, #8B0000, #5C0000, #2D0000)",
//         color: "#fff",
//         minHeight: "100vh",
//         position: "relative",
//         overflow: "hidden",
//     },
//     hiddenInput: {
//         opacity: 0,
//         position: "absolute",
//         left: "-9999px",
//         width: "1px",
//         height: "1px",
//     },
//     header: {
//         display: "flex",
//         flexDirection: "column",
//         alignItems: "center",
//         marginBottom: "30px",
//         position: "relative",
//     },
//     logo: {
//         width: "120px",
//         height: "120px",
//         borderRadius: "50%",
//         border: "4px solid #FFD700",
//         boxShadow: "0px 4px 20px rgba(255, 215, 0, 0.5)",
//         marginBottom: "15px",
//     },
//     eventInfo: {
//         background: "linear-gradient(135deg, rgba(139, 0, 0, 0.9), rgba(92, 0, 0, 0.9))",
//         padding: "25px 50px",
//         borderRadius: "15px",
//         border: "2px solid #FFD700",
//         boxShadow: "0 8px 32px rgba(0, 0, 0, 0.4)",
//     },
//     eventTitle: {
//         fontSize: "32px",
//         fontWeight: "bold",
//         margin: "0 0 10px 0",
//         color: "#FFD700",
//         textShadow: "2px 2px 4px rgba(0, 0, 0, 0.5)",
//     },
//     eventDate: {
//         fontSize: "20px",
//         margin: 0,
//         color: "#FFF",
//         opacity: 0.95,
//     },
//     scannerBox: {
//         width: "100%",
//         maxWidth: "500px",
//         margin: "40px auto",
//         padding: "60px 40px",
//         background: "linear-gradient(145deg, rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0.9))",
//         borderRadius: "25px",
//         boxShadow: "0px 10px 40px rgba(0, 0, 0, 0.5)",
//         border: "3px solid #FFD700",
//     },
//     scannerIcon: {
//         fontSize: "120px",
//         marginBottom: "20px",
//     },
//     scanAnimationContainer: {
//         position: "relative",
//         marginBottom: "25px",
//         height: "140px",
//         display: "flex",
//         justifyContent: "center",
//         alignItems: "center",
//     },
//     cardIconContainer: {
//         filter: "drop-shadow(0 6px 12px rgba(139, 0, 0, 0.2))",
//         animation: "float 3s ease-in-out infinite",
//         display: "flex",
//         justifyContent: "center",
//         alignItems: "center",
//         zIndex: 1,
//     },
//     scanLineContainer: {
//         position: "absolute",
//         width: "100%",
//         height: "100%",
//         display: "flex",
//         justifyContent: "center",
//         alignItems: "center",
//         zIndex: 2,
//         pointerEvents: "none",
//     },
//     scanLine: {
//         width: "120px",
//         height: "3px",
//         background: "linear-gradient(90deg, transparent, #FFD700, transparent)",
//         boxShadow: "0 0 10px #FFD700, 0 0 20px #FFD700",
//         animation: "scan 2.5s ease-in-out infinite",
//     },
//     scanIconContainer: {
//         position: "absolute",
//         bottom: "10px",
//         right: "calc(50% - 60px)",
//         animation: "pulse 2s ease-in-out infinite",
//         filter: "drop-shadow(0 2px 8px rgba(255, 215, 0, 0.5))",
//     },
//     scannerText: {
//         fontSize: "40px",
//         fontWeight: "700",
//         color: "#8B0000",
//         margin: "15px 0",
//     },
//     scannerIndicator: {
//         display: "flex",
//         justifyContent: "center",
//         alignItems: "center",
//         marginTop: "20px",
//     },
//     pulse: {
//         width: "30px",
//         height: "30px",
//         borderRadius: "50%",
//         background: "#FF6B00",
//         animation: "pulse 1.5s ease-in-out infinite",
//         boxShadow: "0 0 20px rgba(255, 107, 0, 0.6)",
//     },
//     readyDot: {
//         width: "30px",
//         height: "30px",
//         borderRadius: "50%",
//         background: "#28a745",
//         boxShadow: "0 0 20px rgba(40, 167, 69, 0.6)",
//     },
//     scanCounter: {
//         marginTop: "25px",
//         fontSize: "20px",
//         color: "#8B0000",
//         fontWeight: "600",
//         padding: "12px 24px",
//         background: "rgba(255, 215, 0, 0.2)",
//         borderRadius: "25px",
//         display: "inline-block",
//     },
//     welcomeContainer: {
//         width: "100%",
//         maxWidth: "550px",
//         margin: "40px auto",
//         animation: "scaleIn 0.5s ease-out",
//     },
//     welcomeCard: {
//         background: "linear-gradient(145deg, rgba(255, 255, 255, 0.98), rgba(255, 255, 255, 0.95))",
//         padding: "60px 40px",
//         borderRadius: "30px",
//         boxShadow: "0px 15px 50px rgba(0, 0, 0, 0.6)",
//         border: "4px solid #FFD700",
//         position: "relative",
//         overflow: "hidden",
//     },
//     tickLarge: {
//         fontSize: "120px",
//         color: "#28a745",
//         fontWeight: "bold",
//         marginBottom: "20px",
//         textShadow: "0 4px 10px rgba(40, 167, 69, 0.4)",
//         animation: "scaleIn 0.4s ease-out",
//     },
//     welcomeText: {
//         fontSize: "36px",
//         color: "#8B0000",
//         margin: "10px 0",
//         fontWeight: "bold",
//     },
//     userName: {
//         fontSize: "48px",
//         color: "#8B0000",
//         margin: "20px 0",
//         fontWeight: "bold",
//         textShadow: "2px 2px 4px rgba(0, 0, 0, 0.1)",
//         wordBreak: "break-word",
//     },
//     successBadge: {
//         display: "inline-block",
//         background: "#28a745",
//         color: "#fff",
//         padding: "18px 45px",
//         borderRadius: "50px",
//         fontSize: "22px",
//         fontWeight: "bold",
//         margin: "20px 0",
//         boxShadow: "0 4px 15px rgba(40, 167, 69, 0.4)",
//     },
//     badgeIcon: {
//         marginRight: "10px",
//         fontSize: "26px",
//     },
//     welcomeSubtext: {
//         fontSize: "20px",
//         color: "#555",
//         marginTop: "15px",
//         fontStyle: "italic",
//     },
//     errorContainer: {
//         width: "100%",
//         maxWidth: "550px",
//         margin: "40px auto",
//         animation: "slideIn 0.4s ease-out",
//     },
//     errorCard: {
//         background: "linear-gradient(145deg, rgba(255, 255, 255, 0.98), rgba(255, 255, 255, 0.95))",
//         padding: "60px 40px",
//         borderRadius: "30px",
//         boxShadow: "0px 15px 50px rgba(0, 0, 0, 0.6)",
//         border: "4px solid #ff4444",
//     },
//     crossLarge: {
//         fontSize: "120px",
//         color: "#ff4444",
//         fontWeight: "bold",
//         marginBottom: "20px",
//         textShadow: "0 4px 10px rgba(255, 68, 68, 0.4)",
//     },
//     errorTitle: {
//         fontSize: "36px",
//         color: "#8B0000",
//         margin: "10px 0 20px 0",
//         fontWeight: "bold",
//     },
//     errorText: {
//         fontSize: "22px",
//         color: "#ff4444",
//         fontWeight: "600",
//         margin: "10px 0",
//     },
//     footerContainer: {
//         position: "fixed",
//         bottom: 0,
//         left: 0,
//         right: 0,
//         display: "flex",
//         justifyContent: "center",
//         width: "100%",
//         padding: "0",
//     },
//     poweredByBox: {
//         background: "transparent",
//         width: "100%",
//         padding: "20px",
//         display: "flex",
//         justifyContent: "center",
//         alignItems: "center",
//         gap: "12px",
//     },
//     poweredByText: {
//         color: "#fff",
//         fontSize: "18px",
//         fontWeight: "500",
//     },
//     footerLogo: {
//         height: "35px",
//         filter: "brightness(1.1)",
//     },
//     companyName: {
//         color: "#fff",
//         fontSize: "18px",
//         fontWeight: "600",
//     },
// };

// const styleSheet = document.createElement("style");
// styleSheet.textContent = `
//     @keyframes pulse {
//         0%, 100% {
//             transform: scale(1);
//             opacity: 1;
//         }
//         50% {
//             transform: scale(1.4);
//             opacity: 0.7;
//         }
//     }
//     @keyframes scaleIn {
//         from {
//             transform: scale(0);
//             opacity: 0;
//         }
//         to {
//             transform: scale(1);
//             opacity: 1;
//         }
//     }
//     @keyframes slideIn {
//         from {
//             transform: translateY(-30px);
//             opacity: 0;
//         }
//         to {
//             transform: translateY(0);
//             opacity: 1;
//         }
//     }
//     @keyframes float {
//         0%, 100% {
//             transform: translateY(0px);
//         }
//         50% {
//             transform: translateY(-10px);
//         }
//     }
//     @keyframes scan {
//         0%, 100% {
//             transform: translateY(-60px);
//             opacity: 0;
//         }
//         10% {
//             opacity: 1;
//         }
//         90% {
//             opacity: 1;
//         }
//         100% {
//             transform: translateY(60px);
//             opacity: 0;
//         }
//     }
// `;
// document.head.appendChild(styleSheet);



